import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api.js';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: (count, err) => {
          if (err instanceof ApiError && ['unauthorized', 'forbidden', 'not_found', 'validation'].includes(err.kind)) return false;
          return count < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

/** Query keys are always workspace-prefixed so switching workspaces never mixes data. */
export const qk = {
  me: ['me'] as const,
  sessions: ['me', 'sessions'] as const,
  ws: (w: string, ...rest: unknown[]) => ['w', w, ...rest] as const,
  platform: (...rest: unknown[]) => ['platform', ...rest] as const,
};

/**
 * Workspace-scoped query keys shared by all pages. Invalidating a prefix (e.g. `wk.projects(w)`) also
 * refreshes nested keys (project detail, units).
 */
export const wk = {
  workspace: (w: string) => qk.ws(w, 'workspace'),
  home: (w: string) => qk.ws(w, 'home'),
  modules: (w: string) => qk.ws(w, 'modules'),
  setup: (w: string) => qk.ws(w, 'setup'),
  /** Per-module onboarding; without `module` it is the prefix for all modules. */
  onboarding: (w: string, module?: string) => (module ? qk.ws(w, 'onboarding', module) : qk.ws(w, 'onboarding')),
  audit: (w: string, cursor?: string) => qk.ws(w, 'audit', cursor ?? null),
  projects: (w: string) => qk.ws(w, 'projects'),
  project: (w: string, projectId: string) => qk.ws(w, 'projects', projectId),
  units: (w: string, projectId: string) => qk.ws(w, 'projects', projectId, 'units'),
  schedules: (w: string) => qk.ws(w, 'schedules'),
  scheduleStatus: (w: string, q: { channel?: string; projectId?: string }) => qk.ws(w, 'schedules', 'status', q),
  providers: (w: string) => qk.ws(w, 'integrations', 'providers'),
  connections: (w: string) => qk.ws(w, 'integrations', 'connections'),
  /** Telnyx setup (under connections: any connection/endpoint invalidation refreshes it). */
  telnyx: (w: string) => qk.ws(w, 'integrations', 'connections', 'telnyx'),
  receipts: (w: string, cursor?: string) => qk.ws(w, 'integrations', 'receipts', cursor ?? null),
  webhooks: (w: string) => qk.ws(w, 'webhooks'),
  deliveries: (w: string, webhookId: string, cursor?: string) => qk.ws(w, 'webhooks', webhookId, 'deliveries', cursor ?? null),
  agents: (w: string) => qk.ws(w, 'agents'),
  compiled: (w: string, q: { projectId?: string; channel: string; useDrafts: boolean }) => qk.ws(w, 'agents', 'compiled', q),
  leads: (w: string, q?: Record<string, unknown>) => (q ? qk.ws(w, 'leads', q) : qk.ws(w, 'leads')),
  /** Funnel + qualification of one opportunity (under `leads(w)`: invalidating the list refreshes it too). */
  leadFunnel: (w: string, interestId: string) => qk.ws(w, 'leads', 'funnel', interestId),
  leadAttribution: (w: string, interestId: string) => qk.ws(w, 'leads', 'attribution', interestId),
  leadCrm: (w: string, interestId: string) => qk.ws(w, 'leads', 'crm', interestId),
  leadObjections: (w: string, interestId: string) => qk.ws(w, 'leads', 'objections', interestId),
  /** Setter §06.10.5: estimated AI cost of one opportunity (under `leads(w)`). */
  leadAiCost: (w: string, interestId: string) => qk.ws(w, 'leads', 'ai-cost', interestId),
  prospectBudget: (w: string, projectId: string) => qk.ws(w, 'projects', projectId, 'prospect-budget'),
  voicePersona: (w: string, projectId: string) => qk.ws(w, 'projects', projectId, 'voice-persona'),
  prospectBudgetReviews: (w: string, q?: Record<string, unknown>) => (q ? qk.ws(w, 'prospect-budget-reviews', q) : qk.ws(w, 'prospect-budget-reviews')),
  /** Setter §06.10.3–4: contact-level AI attention (paused/blocked for possible abuse) and the review queue. */
  aiAttention: (w: string, contactId: string) => qk.ws(w, 'ai-attention', contactId),
  aiAbuseCases: (w: string, q?: Record<string, unknown>) => (q ? qk.ws(w, 'ai-abuse-cases', q) : qk.ws(w, 'ai-abuse-cases')),
  /** Setter §06.10.1–2, §09.12: per-project call controls. */
  callControls: (w: string, projectId: string) => qk.ws(w, 'projects', projectId, 'call-controls'),
  lossReasons: (w: string) => qk.ws(w, 'loss-reasons'),
  qualificationConfig: (w: string, projectId: string) => qk.ws(w, 'projects', projectId, 'qualification'),
  conversations: (w: string, q?: Record<string, unknown>) => (q ? qk.ws(w, 'conversations', 'list', q) : qk.ws(w, 'conversations')),
  conversation: (w: string, id: string) => qk.ws(w, 'conversations', 'detail', id),
  calls: (w: string, q?: Record<string, unknown>) => (q ? qk.ws(w, 'calls', q) : qk.ws(w, 'calls')),
  appointments: (w: string, q?: Record<string, unknown>) => (q ? qk.ws(w, 'appointments', q) : qk.ws(w, 'appointments')),
  handoffs: (w: string) => qk.ws(w, 'handoffs'),
  members: (w: string) => qk.ws(w, 'team', 'members'),
  /** Live Call Copilot; `copilot(w)` is the prefix for everything below. */
  copilot: (w: string) => qk.ws(w, 'copilot'),
  copilotSessions: (w: string, q?: Record<string, unknown>) => (q ? qk.ws(w, 'copilot', 'sessions', 'list', q) : qk.ws(w, 'copilot', 'sessions', 'list')),
  copilotSession: (w: string, id: string) => qk.ws(w, 'copilot', 'sessions', 'detail', id),
  copilotPostCall: (w: string, id: string) => qk.ws(w, 'copilot', 'sessions', 'detail', id, 'post-call'),
  copilotSimulator: (w: string) => qk.ws(w, 'copilot', 'simulator'),
  copilotPlaybooks: (w: string) => qk.ws(w, 'copilot', 'playbooks'),
  copilotPlaybook: (w: string, id: string) => qk.ws(w, 'copilot', 'playbooks', id),
  copilotConfig: (w: string) => qk.ws(w, 'copilot', 'config'),
  invitations: (w: string) => qk.ws(w, 'team', 'invitations'),
  /** Advisor softphone (plan B3): the advisor's own state, and the admin seats view. */
  softphone: (w: string) => qk.ws(w, 'softphone'),
  softphoneAdmin: (w: string) => qk.ws(w, 'softphone-admin'),
  /** Plan B7: advisor call history, team presence and the advisor call report. */
  advisorCalls: (w: string, q?: Record<string, unknown>) => (q ? qk.ws(w, 'advisor-calls', 'list', q) : qk.ws(w, 'advisor-calls')),
  advisorCallReport: (w: string, q: Record<string, unknown>) => qk.ws(w, 'advisor-calls', 'report', q),
  teamPresence: (w: string) => qk.ws(w, 'softphone-team'),
};

export const pk = {
  overview: () => qk.platform('overview'),
  diff: () => qk.platform('diff'),
  release: (version: number) => qk.platform('release', version),
  models: () => qk.platform('models'),
  /** HubSpot calling extension registration (plan B6). */
  hubspotCalling: () => qk.platform('hubspot-calling'),
  /** «Equipo Estateflow»: platform capabilities and pending platform invitations. */
  team: () => qk.platform('team'),
};
